import type { FormatType } from '@revelio/iso-utility';

import { getFormatter, getMaxTextWidth, isDateValid } from '../../utilities';
import type { LineData } from './line-chart';

export const formatDataDates = (data: LineData<string>[]): LineData<Date>[] =>
  data.map((d) => ({
    ...d,
    values: d.values
      .map((v) => ({ ...v, date: new Date(v.date) }))
      .filter((d) => isDateValid(d.date)),
  }));

/**
 * Create a new array with unique labels (append “ (n)” to duplicates) so
 * different series that share the same label are treated distinctly across
 * scales, paths, and tooltips. Implemented as an IIFE to avoid mutating the
 * original objects.
 */
export const dedupeDataDimLabels = (
  data: LineData<Date>[]
): LineData<Date>[] => {
  const labelCounts = new Map<string, number>();

  return data.map((series) => {
    const count = labelCounts.get(series.label) ?? 0;
    labelCounts.set(series.label, count + 1);

    const uniqueLabel =
      count === 0 ? series.label : `${series.label} (${count + 1})`;
    return { ...series, label: uniqueLabel };
  });
};

const RIGHT_LABEL_OFFSET_X = 8;
const RIGHT_LABEL_PADDING_RIGHT = 2;
const DIFF_LABEL_FONT_SIZE_PX = 14;

const roundToPrecision = (n: number, decimals = 10): number =>
  Number(n.toFixed(decimals));

export const getLineDiffText = ({
  showDiffLabel,
  data,
  format,
}: {
  showDiffLabel: boolean;
  data: LineData<Date>[];
  format: FormatType;
}) => {
  let firstDiffValue: number | null = null;
  let secondDiffValue: number | null = null;
  let text: string | null = null;
  let textWidth = 0;

  if (showDiffLabel && data.length === 2) {
    const series1Values = data[0]?.values ?? [];
    const series2Values = data[1]?.values ?? [];
    firstDiffValue = series1Values[series1Values.length - 1]?.value ?? null;
    secondDiffValue = series2Values[series2Values.length - 1]?.value ?? null;

    if (firstDiffValue !== null && secondDiffValue !== null) {
      const formatter = getFormatter(format, {
        values: [firstDiffValue, secondDiffValue],
      });
      const diff = roundToPrecision(firstDiffValue - secondDiffValue);

      if (diff === 0) {
        text = formatter(0);
      } else {
        text = `${diff < 0 ? '−' : '+'}${formatter(Math.abs(diff))}`;
      }

      textWidth = getMaxTextWidth({
        texts: [text],
        fontSize: DIFF_LABEL_FONT_SIZE_PX,
      });
    }
  }
  const rightGutter =
    textWidth > 0
      ? Math.ceil(RIGHT_LABEL_OFFSET_X + textWidth + RIGHT_LABEL_PADDING_RIGHT)
      : 0;

  return { firstDiffValue, secondDiffValue, text, rightGutter };
};
